/*!
 * Dr.Golf Swing Assessment — DIAGNOSTIC ENGINE
 * Pure, deterministic, configuration-driven. No DOM, no network, no randomness.
 * Rule-based indicators only — not a scientific or biomechanical measurement.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else { root.DrGolf = root.DrGolf || {}; root.DrGolf.engine = factory(); }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DIMS = ['T', 'C', 'P', 'E', 'O'];
  var FAMILY_DIM = { tempo: 'T', connection: 'C' };
  var DIM_FAMILY = { T: 'tempo', C: 'connection' };
  var STRENGTH_RANK = { tentative: 0, moderate: 1, strong: 2 };

  function key(qid, oid) { return qid + ':' + oid; }

  function findOption(q, oid) {
    for (var i = 0; i < q.options.length; i++) if (q.options[i].id === oid) return q.options[i];
    return null;
  }

  /** Theoretical maximum per dimension, derived from the question config. */
  function maxScores(questions) {
    var max = {};
    DIMS.forEach(function (d) { max[d] = 0; });
    questions.forEach(function (q) {
      DIMS.forEach(function (d) {
        var best = 0;
        q.options.forEach(function (o) { best = Math.max(best, (o.w && o.w[d]) || 0); });
        max[d] += best;
      });
    });
    return max;
  }

  function level(score, cfg) {
    if (score >= cfg.levels.high) return 'high';
    if (score >= cfg.levels.moderate) return 'moderate';
    return 'low';
  }

  /** Condition DSL — see drgolf-content.js header. */
  function matches(when, ctx) {
    if (!when) return true;
    if (Array.isArray(when)) {
      for (var i = 0; i < when.length; i++) if (matches(when[i], ctx)) return true;
      return false;
    }
    var sel = ctx.sel;
    if (when.all && !when.all.every(function (k) { return sel[k]; })) return false;
    if (when.any && !when.any.some(function (k) { return sel[k]; })) return false;
    if (when.none && when.none.some(function (k) { return sel[k]; })) return false;
    if (when.min) {
      var mins = Array.isArray(when.min) ? when.min : [when.min];
      for (var m = 0; m < mins.length; m++) {
        var c = mins[m].of.filter(function (k) { return sel[k]; }).length;
        if (c < mins[m].n) return false;
      }
    }
    if (when.dims) {
      for (var d in when.dims) {
        var r = when.dims[d], v = ctx.norm[d];
        if (r.gte != null && !(v >= r.gte)) return false;
        if (r.lt != null && !(v < r.lt)) return false;
      }
    }
    if (when.support) {
      for (var s in when.support) {
        var n = ctx.support[s].count;
        if (when.support[s].gte != null && !(n >= when.support[s].gte)) return false;
      }
    }
    return true;
  }

  function matchedCount(list, sel) {
    return (list || []).filter(function (k) { return sel[k]; }).length;
  }

  function pickDiagnosis(list, ctx) {
    for (var i = 0; i < list.length; i++) if (matches(list[i].when, ctx)) return list[i];
    return null;
  }

  /** Evidence strength for a family dimension. */
  function strengthOf(sup, cfg) {
    var e = cfg;
    if (sup.count >= e.strong.support && sup.technical >= e.strong.technical &&
        sup.contra.length <= e.strong.maxContra && sup.count - sup.contra.length >= e.strong.support) return 'strong';
    if (sup.count >= e.moderate.support && sup.count - sup.contra.length >= 1) return 'moderate';
    return 'tentative';
  }

  function minStrength(a, b) { return STRENGTH_RANK[a] <= STRENGTH_RANK[b] ? a : b; }

  /** Normalize an answers object / string into { q1: 'B', … }. */
  function parseAnswers(input, questions) {
    var out = {};
    if (typeof input === 'string') {
      var s = input.toUpperCase();
      questions.forEach(function (q, i) { if (s[i] && s[i] !== '-') out[q.id] = s[i]; });
      return out;
    }
    questions.forEach(function (q) { if (input && input[q.id]) out[q.id] = String(input[q.id]).toUpperCase(); });
    return out;
  }

  function encodeAnswers(answers, questions) {
    return questions.map(function (q) { return answers[q.id] || '-'; }).join('');
  }

  function isComplete(answers, questions) {
    return questions.every(function (q) { return answers[q.id] && findOption(q, answers[q.id]); });
  }

  /**
   * evaluate(answers, config, content, options?) → result
   * options.preferFamily: 'tempo' | 'connection' — golfer's own choice (starter / maintenance modes only)
   */
  function evaluate(input, config, content, options) {
    options = options || {};
    var questions = config.questions, ecfg = config.engine;
    var answers = parseAnswers(input, questions);
    if (!isComplete(answers, questions)) {
      return { complete: false, answers: answers, answered: Object.keys(answers).length };
    }

    var raw = {}, sel = {}, chosen = [];
    DIMS.forEach(function (d) { raw[d] = 0; });
    questions.forEach(function (q, idx) {
      var o = findOption(q, answers[q.id]);
      sel[key(q.id, o.id)] = true;
      chosen.push({ q: q, o: o, index: idx, key: key(q.id, o.id) });
      DIMS.forEach(function (d) { raw[d] += (o.w && o.w[d]) || 0; });
    });

    var max = maxScores(questions), norm = {}, levels = {};
    DIMS.forEach(function (d) {
      norm[d] = max[d] ? Math.round((raw[d] / max[d]) * 100) : 0;
      levels[d] = level(norm[d], ecfg);
    });

    // Family support & contradictions
    var support = {};
    ['T', 'C'].forEach(function (d) {
      var s = { count: 0, technical: 0, answers: [], contra: [] };
      chosen.forEach(function (c) {
        var w = (c.o.w && c.o.w[d]) || 0;
        if (w >= ecfg.supportMinWeight) {
          s.count++; s.answers.push(c.key);
          if (c.q.type === 'technical') s.technical++;
        }
        if (c.o.contra && c.o.contra.indexOf(d) !== -1) s.contra.push(c.key);
      });
      s.strength = strengthOf(s, ecfg);
      support[d] = s;
    });

    var ctx = { sel: sel, norm: norm, support: support, levels: levels };

    // ---------- Mode & family ----------
    var diff = norm.T - norm.C;
    var leadDim = diff >= 0 ? 'T' : 'C';
    var m = ecfg.maintenance;
    var mode, family, alternateFamily = null, context = null;

    var lowEverything = norm.T < m.tcBelow && norm.C < m.tcBelow &&
      norm.P < m.othersBelow && norm.E < m.othersBelow && norm.O < m.othersBelow &&
      support.T.count <= m.maxSupport && support.C.count <= m.maxSupport;

    function evidencePick() {
      if (Math.abs(diff) >= ecfg.leadMargin) return DIM_FAMILY[leadDim];
      if (support.T.count !== support.C.count) return support.T.count > support.C.count ? 'tempo' : 'connection';
      if (norm.T !== norm.C) return norm.T > norm.C ? 'tempo' : 'connection';
      return ecfg.starterTiebreak;
    }

    if (lowEverything) {
      mode = 'maintenance';
      family = evidencePick();
    } else if (Math.abs(diff) >= ecfg.leadMargin && support[leadDim].count >= ecfg.minSupportForPattern) {
      mode = 'single';
      family = DIM_FAMILY[leadDim];
    } else if (norm.T >= ecfg.combinedMin && norm.C >= ecfg.combinedMin && Math.abs(diff) < ecfg.leadMargin) {
      mode = 'combined';
      family = 'combined';
    } else {
      mode = 'starter';
      family = evidencePick();
      if (levels.P === 'high') context = 'pressure';
    }

    var evidencePicked = family; // what the evidence chose, before any golfer preference
    if ((mode === 'starter' || mode === 'maintenance') && (options.preferFamily === 'tempo' || options.preferFamily === 'connection')) {
      family = options.preferFamily;
    }
    if (mode === 'starter' || mode === 'maintenance') alternateFamily = family === 'tempo' ? 'connection' : 'tempo';

    // ---------- Diagnosis ----------
    var dx, subDiagnoses = null, strength;
    if (mode === 'single') {
      dx = pickDiagnosis(content.diagnoses[family], ctx) || content.fallbacks[family];
      strength = support[FAMILY_DIM[family]].strength;
      if (dx === content.fallbacks[family] && strength === 'strong') strength = 'moderate';
    } else if (mode === 'combined') {
      dx = content.combined;
      subDiagnoses = {
        tempo: pickDiagnosis(content.diagnoses.tempo, ctx) || content.fallbacks.tempo,
        connection: pickDiagnosis(content.diagnoses.connection, ctx) || content.fallbacks.connection
      };
      strength = minStrength(support.T.strength, support.C.strength);
    } else if (mode === 'starter') {
      dx = content.starter[family];
      strength = 'tentative';
    } else {
      dx = content.maintenance;
      strength = 'tentative';
    }

    var summary = dx.summary;
    if (mode === 'starter' && context === 'pressure' && dx.pressure) summary = dx.pressure;

    // ---------- Evidence statements (only from selected answers) ----------
    var byKey = {};
    chosen.forEach(function (c) { byKey[c.key] = c; });
    var picked = [], seen = {};
    function push(k) {
      if (picked.length >= ecfg.maxEvidence || seen[k] || !sel[k]) return;
      var c = byKey[k];
      if (!c || !c.o.evidence) return;
      seen[k] = true;
      picked.push({ key: k, question: c.q.id, option: c.o.id, text: c.o.evidence });
    }
    function byWeight(dim) {
      return chosen.filter(function (c) { return ((c.o.w && c.o.w[dim]) || 0) > 0; })
        .sort(function (a, b) {
          var wa = a.o.w[dim], wb = b.o.w[dim];
          if (wb !== wa) return wb - wa;
          if (a.q.type !== b.q.type) return a.q.type === 'technical' ? -1 : 1;
          return a.index - b.index;
        }).map(function (c) { return c.key; });
    }
    function bestSwing() { chosen.forEach(function (c) { if (c.q.id === 'q6') push(c.key); }); }

    if (mode === 'single') {
      (dx.evidenceFrom || []).forEach(function (k) { if (picked.length < ecfg.maxEvidence - 1) push(k); });
      support[FAMILY_DIM[family]].answers.forEach(function (k) { if (picked.length < ecfg.maxEvidence - 1) push(k); });
      bestSwing();
      byWeight(FAMILY_DIM[family]).forEach(push);
    } else if (mode === 'combined') {
      var a = (subDiagnoses.tempo.evidenceFrom || []).concat(support.T.answers).filter(function (k) { return sel[k]; });
      var b = (subDiagnoses.connection.evidenceFrom || []).concat(support.C.answers).filter(function (k) { return sel[k]; });
      for (var i = 0; i < 6 && picked.length < ecfg.maxEvidence - 1; i++) { if (a[i]) push(a[i]); if (b[i]) push(b[i]); }
      bestSwing();
      byWeight('T').concat(byWeight('C')).forEach(push);
    } else if (mode === 'starter') {
      if (context === 'pressure') byWeight('P').slice(0, 2).forEach(push);
      byWeight(FAMILY_DIM[family]).slice(0, 2).forEach(push);
      bestSwing();
      byWeight(FAMILY_DIM[family]).forEach(push);
      byWeight('P').forEach(push);
    } else {
      // maintenance: reflect the stability they reported
      chosen.forEach(function (c) { if (c.o.contra && c.o.contra.length) push(c.key); });
      bestSwing();
    }

    // One honest counterpoint when the evidence is not fully consistent
    var counterEvidence = null;
    if (mode === 'single' || mode === 'combined') {
      var dims = mode === 'single' ? [FAMILY_DIM[family]] : ['T', 'C'];
      for (var di = 0; di < dims.length && !counterEvidence; di++) {
        var ck = support[dims[di]].contra[0];
        if (ck && byKey[ck].o.evidence) counterEvidence = { key: ck, question: byKey[ck].q.id, option: byKey[ck].o.id, text: byKey[ck].o.evidence };
      }
    }

    // ---------- Secondary findings ----------
    var found = [];
    (content.secondary || []).forEach(function (f, order) {
      if (!matches(f.when, ctx)) return;
      var lv = levels[f.dim];
      found.push({
        id: f.id, name: f.name, group: f.group, advice: f.advice, text: f.text,
        level: lv,
        levelText: (f.levelText || '').replace('{level}', content.levelWords[lv]),
        triggers: (f.signals || []).filter(function (k) { return sel[k]; }),
        score: matchedCount(f.signals, sel) * 10 + norm[f.dim] / 10,
        order: order
      });
    });
    var bestByGroup = {};
    found.forEach(function (f) {
      var cur = bestByGroup[f.group];
      if (!cur || f.score > cur.score || (f.score === cur.score && f.order < cur.order)) bestByGroup[f.group] = f;
    });
    var secondary = Object.keys(bestByGroup).map(function (g) { return bestByGroup[g]; })
      .sort(function (x, y) { return y.score - x.score || x.order - y.order; })
      .slice(0, ecfg.maxSecondary)
      .map(function (f) { delete f.score; delete f.order; return f; });

    // ---------- Free advice ----------
    var adviceIds = [];
    function addAdvice(id) { if (id && content.advice[id] && adviceIds.indexOf(id) === -1 && adviceIds.length < 3) adviceIds.push(id); }
    if (context === 'pressure') addAdvice('pressure');
    if (mode === 'maintenance') addAdvice('maintenance');
    secondary.forEach(function (f) { if (adviceIds.length < 2) addAdvice(f.advice); });
    if (family === 'combined') { addAdvice('tempo'); addAdvice('connection'); }
    else addAdvice(family);

    // ---------- Products & protocol ----------
    var products = config.families[family].products.slice();
    var protocolId = dx.protocol;

    return {
      complete: true,
      version: config.version,
      answers: answers,
      answerString: encodeAnswers(answers, questions),
      raw: raw, max: max, norm: norm, levels: levels,
      support: support,
      mode: mode,
      family: family,
      evidenceFamily: evidencePicked,
      alternateFamily: alternateFamily,
      context: context,
      strength: strength,
      diagnosis: { id: dx.id, name: dx.name, short: dx.short, summary: summary, meaning: dx.meaning, problemLine: dx.problemLine },
      subDiagnoses: subDiagnoses && {
        tempo: { id: subDiagnoses.tempo.id, name: subDiagnoses.tempo.name, short: subDiagnoses.tempo.short },
        connection: { id: subDiagnoses.connection.id, name: subDiagnoses.connection.name, short: subDiagnoses.connection.short }
      },
      evidence: picked,
      counterEvidence: counterEvidence,
      secondary: secondary,
      advice: adviceIds,
      protocol: protocolId,
      products: products,
      profileId: mode + ':' + dx.id
    };
  }

  return {
    evaluate: evaluate,
    maxScores: maxScores,
    matches: matches,
    parseAnswers: parseAnswers,
    encodeAnswers: encodeAnswers,
    isComplete: isComplete,
    DIMS: DIMS
  };
});
